if (!customElements.get('homepage-social-media')) {
  customElements.define(
    'homepage-social-media',
    class HomepageSocialMedia extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-slider]');
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.hideControlsMs = 1400;
        this.settleMs = 620;

        if (!this.slider) return;

        this.swipeDesktop = Math.max(1, parseInt(this.dataset.swipeDesktop, 10) || 3);
        this.swipeMobile = Math.max(1, parseInt(this.dataset.swipeMobile, 10) || 2);
        this.setupLoop();
        if (this.cards.length === 0) return;

        if (!this.initialized) {
          this.initialized = true;
          this.onResize = this.onResize.bind(this);
          this.onPointerDown = this.onPointerDown.bind(this);
          this.onPointerMove = this.onPointerMove.bind(this);
          this.onPointerUp = this.onPointerUp.bind(this);
          this.onWheel = this.onWheel.bind(this);
          this.onMediaPointerUp = this.onMediaPointerUp.bind(this);
          this.onMediaClick = this.onMediaClick.bind(this);
          this.onMediaKeydown = this.onMediaKeydown.bind(this);

          window.addEventListener('resize', this.onResize);

          this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goTo(this.realStart + index));
          });

          this.enableDrag();
        }

        this.bindMedia();
        requestAnimationFrame(() => this.goTo(this.activeIndex, false));
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        this.disableDrag();
        this.clearHideTimers();
        this.cancelSettle();
        this.initialized = false;
      }

      setupLoop() {
        this.slider.querySelectorAll('[data-clone]').forEach((node) => node.remove());

        const realCards = Array.from(this.slider.querySelectorAll('[data-card]'));
        this.realCount = realCards.length;
        this.looping = this.realCount > 0;
        this.copiesEachSide = Math.max(
          1,
          Math.ceil((8 + Math.max(this.swipeDesktop, this.swipeMobile)) / Math.max(this.realCount, 1))
        );

        if (this.looping) {
          const prefix = document.createDocumentFragment();
          const suffix = document.createDocumentFragment();

          for (let copy = 0; copy < this.copiesEachSide; copy++) {
            realCards.forEach((card) => prefix.appendChild(this.cloneCard(card)));
          }
          for (let copy = 0; copy < this.copiesEachSide; copy++) {
            realCards.forEach((card) => suffix.appendChild(this.cloneCard(card)));
          }

          this.slider.insertBefore(prefix, realCards[0]);
          this.slider.appendChild(suffix);
        }

        this.cards = Array.from(this.slider.querySelectorAll('[data-card]'));
        this.realStart = this.looping ? this.realCount * this.copiesEachSide : 0;
        this.activeIndex = this.realStart;
      }

      cloneCard(card) {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('[data-media-bound]').forEach((el) => el.removeAttribute('data-media-bound'));
        return clone;
      }

      bindMedia() {
        this.mediaItems = Array.from(this.querySelectorAll('[data-ugc-media]'));
        this.mediaItems.forEach((media) => {
          const video = media.querySelector('video');
          if (!video) return;

          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.playsInline = true;
          video.controls = false;

          if (media.getAttribute('data-media-bound') === 'true') return;
          media.setAttribute('data-media-bound', 'true');

          video.addEventListener('play', () => this.setMediaPlaying(media, true));
          video.addEventListener('pause', () => this.setMediaPlaying(media, false));
          video.addEventListener('ended', () => {
            video.currentTime = 0;
            this.setMediaPlaying(media, false);
          });
        });

        if (this.mediaDelegated) return;
        this.mediaDelegated = true;
        this.slider.addEventListener('pointerup', this.onMediaPointerUp);
        this.slider.addEventListener('click', this.onMediaClick);
        this.slider.addEventListener('keydown', this.onMediaKeydown);
      }

      onMediaPointerUp(event) {
        const toggle = event.target.closest('[data-media-toggle]');
        if (!toggle || !this.slider.contains(toggle)) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.didDrag) return;

        const media = toggle.closest('[data-ugc-media]');
        const video = media?.querySelector('video');
        if (!media || !video) return;
        this.toggleMedia(media, video);
      }

      onMediaClick(event) {
        if (!event.target.closest('[data-media-toggle]')) return;
        event.preventDefault();
      }

      onMediaKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const toggle = event.target.closest('[data-media-toggle]');
        if (!toggle || !this.slider.contains(toggle)) return;

        event.preventDefault();
        const media = toggle.closest('[data-ugc-media]');
        const video = media?.querySelector('video');
        if (!media || !video) return;
        this.toggleMedia(media, video);
      }

      toggleMedia(media, video) {
        if (video.paused) {
          this.pauseAll(video);
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => this.setMediaPlaying(media, false));
          }
          return;
        }

        video.pause();
      }

      pauseAll(exceptVideo) {
        this.mediaItems.forEach((media) => {
          const video = media.querySelector('video');
          if (!video || video === exceptVideo) return;
          video.pause();
        });
      }

      setMediaPlaying(media, playing) {
        media.classList.toggle('is-playing', playing);
        const toggle = media.querySelector('[data-media-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
          toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        }

        if (!playing) {
          media.classList.remove('is-controls-hidden');
          this.clearHideTimer(media);
          return;
        }

        media.classList.remove('is-controls-hidden');
        this.scheduleHideControls(media);
      }

      scheduleHideControls(media) {
        this.clearHideTimer(media);
        media._hideTimer = setTimeout(() => {
          if (media.classList.contains('is-playing')) {
            media.classList.add('is-controls-hidden');
          }
        }, this.hideControlsMs);
      }

      clearHideTimer(media) {
        if (media._hideTimer) {
          clearTimeout(media._hideTimer);
          media._hideTimer = null;
        }
      }

      clearHideTimers() {
        this.mediaItems?.forEach((media) => this.clearHideTimer(media));
      }

      onResize() {
        this.goTo(this.activeIndex, false);
      }

      cardLeft(card) {
        const sliderRect = this.slider.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        return this.slider.scrollLeft + (cardRect.left - sliderRect.left);
      }

      cardStep() {
        if (this.cards.length < 2) {
          const card = this.cards[0];
          if (!card) return 0;
          const gap = parseFloat(getComputedStyle(this.slider).columnGap || getComputedStyle(this.slider).gap) || 0;
          return card.getBoundingClientRect().width + gap;
        }

        return Math.abs(this.cards[1].getBoundingClientRect().left - this.cards[0].getBoundingClientRect().left);
      }

      peekClip() {
        const card = this.cards[0];
        if (!card) return 0;

        const width = card.getBoundingClientRect().width;
        const step = this.cardStep();
        if (width <= 0 || step <= 0) return 0;

        const gap = Math.max(0, step - width);
        const viewport = this.slider.clientWidth;
        let n = Math.max(0, Math.floor((viewport - gap) / step));
        while (n > 0 && viewport - n * step - gap < 0) n -= 1;
        const leftover = Math.max(0, viewport - n * step - gap);
        return width - leftover / 2;
      }

      cardOffset(index) {
        const card = this.cards[index];
        if (!card) return 0;
        return this.cardLeft(card) + this.peekClip();
      }

      setActive(index) {
        this.activeIndex = index;
        this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));

        const realIndex = this.realCount > 0 ? ((index - this.realStart) % this.realCount + this.realCount) % this.realCount : 0;
        this.dots.forEach((dot, i) => {
          const isActive = i === realIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      easeOutQuart(t) {
        const rest = 1 - t;
        return 1 - rest * rest * rest * rest;
      }

      cancelSettle() {
        if (this.settleFrame) {
          cancelAnimationFrame(this.settleFrame);
          this.settleFrame = null;
        }
        this.settling = false;
      }

      wrappedIndex(index) {
        if (!this.looping || this.realCount < 1) return index;
        let next = index;
        while (next < this.realStart) next += this.realCount;
        while (next >= this.realStart + this.realCount) next -= this.realCount;
        return next;
      }

      jumpTo(index) {
        this.setActive(index);
        this.slider.scrollLeft = this.cardOffset(index);
      }

      normalizeIndex() {
        if (!this.looping || this.realCount < 1) return;
        const next = this.wrappedIndex(this.activeIndex);
        if (next === this.activeIndex) return;
        this.jumpTo(next);
      }

      goTo(index, animate = true) {
        const next = Math.max(0, Math.min(this.cards.length - 1, index));
        const target = this.cardOffset(next);
        const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
        const clampedTarget = Math.max(0, Math.min(maxScroll, target));
        const blocked = Math.abs(clampedTarget - this.slider.scrollLeft) < 2 && next !== this.activeIndex;

        if (this.looping && blocked) {
          this.cancelSettle();
          this.jumpTo(this.wrappedIndex(next));
          return;
        }

        this.setActive(next);

        if (!animate) {
          this.cancelSettle();
          this.slider.scrollLeft = clampedTarget;
          this.normalizeIndex();
          return;
        }

        this.animateScroll(clampedTarget, () => this.normalizeIndex());
      }

      animateScroll(target, after) {
        this.cancelSettle();
        const start = this.slider.scrollLeft;
        const distance = target - start;
        if (Math.abs(distance) < 1) {
          after?.();
          return;
        }

        const duration = this.settleMs;
        const startTime = performance.now();
        this.settling = true;

        const step = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          this.slider.scrollLeft = start + distance * this.easeOutQuart(t);
          if (t < 1) {
            this.settleFrame = requestAnimationFrame(step);
            return;
          }
          this.slider.scrollLeft = target;
          this.settling = false;
          this.settleFrame = null;
          after?.();
        };

        this.settleFrame = requestAnimationFrame(step);
      }

      dragThreshold() {
        return Math.max(52, this.cardStep() * 0.22);
      }

      swipeStep() {
        return window.matchMedia('(max-width: 749px)').matches ? this.swipeMobile : this.swipeDesktop;
      }

      enableDrag() {
        this.slider.addEventListener('pointerdown', this.onPointerDown);
        this.slider.addEventListener('wheel', this.onWheel, { passive: false });
      }

      disableDrag() {
        this.slider.removeEventListener('pointerdown', this.onPointerDown);
        this.stopTracking();
        this.slider.removeEventListener('wheel', this.onWheel);
        this.slider.removeEventListener('pointerup', this.onMediaPointerUp);
        this.slider.removeEventListener('click', this.onMediaClick);
        this.slider.removeEventListener('keydown', this.onMediaKeydown);
        this.mediaDelegated = false;
      }

      stopTracking() {
        if (this.pointerId != null && this.slider.hasPointerCapture(this.pointerId)) {
          this.slider.releasePointerCapture(this.pointerId);
        }
        this.pointerId = null;
        this.tracking = false;
        this.dragging = false;
        this.slider.classList.remove('is-dragging');
        this.slider.removeEventListener('pointermove', this.onPointerMove);
        this.slider.removeEventListener('pointerup', this.onPointerUp);
        this.slider.removeEventListener('pointercancel', this.onPointerUp);
      }

      finishSwipe(direction) {
        this.stopTracking();
        this.goTo(this.activeIndex + direction * this.swipeStep(), true);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        if (this.settling) {
          this.cancelSettle();
          this.slider.scrollLeft = this.cardOffset(this.activeIndex);
        }

        this.tracking = true;
        this.dragging = false;
        this.didDrag = false;
        this.pointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragScrollLeft = this.slider.scrollLeft;
        this.slider.addEventListener('pointermove', this.onPointerMove);
        this.slider.addEventListener('pointerup', this.onPointerUp);
        this.slider.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.tracking) return;
        const delta = event.clientX - this.dragStartX;

        if (!this.dragging) {
          if (Math.abs(delta) < 8) return;
          this.dragging = true;
          this.didDrag = true;
          this.cancelSettle();
          this.slider.classList.add('is-dragging');
          this.slider.setPointerCapture(this.pointerId);
        }

        event.preventDefault();

        const threshold = this.dragThreshold();
        if (delta <= -threshold) {
          this.finishSwipe(1);
          return;
        }
        if (delta >= threshold) {
          this.finishSwipe(-1);
          return;
        }

        this.slider.scrollLeft = this.dragScrollLeft - delta;
      }

      onPointerUp() {
        if (!this.tracking) return;
        const wasDragging = this.dragging;
        this.stopTracking();
        if (!wasDragging) return;
        this.goTo(this.activeIndex, true);
      }

      onWheel(event) {
        if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
        event.preventDefault();
        if (this.dragging) return;
        if (this.wheelLock) return;

        this.wheelLock = true;
        const next = event.deltaX > 0 ? this.activeIndex + this.swipeStep() : this.activeIndex - this.swipeStep();
        this.goTo(next, true);
        clearTimeout(this.wheelTimer);
        this.wheelTimer = setTimeout(() => {
          this.wheelLock = false;
        }, 180);
      }
    }
  );
}
